import Axios, { AxiosError } from 'axios';
import { APP_CONFIG } from '@/common';
import { PrecisePriceConfig } from '@/entities/config';
import type {
	PlateToVinServiceRequest,
	PlateToVinServiceResponseData,
	TradeInAPIResponse, TradeInValueRequest,
} from '@/types/tradepending';
import { Locales } from '@/locales';
import Translations from '@/locales/en.json';
import { CbcResponse, CreditRequest, VerificationCodeResponse } from '@/types/cbc';
import { WebsiteResponse, DealerResponse } from '@/types/core';

const webAPI = Axios.create({
	baseURL: `${APP_CONFIG.API_URL}website`,
});

interface WarmupResponse {
	status: string;
}

export class WebsiteService {
	websiteId: string

	constructor({ apiKey, websiteId, ppSSOKey }: PrecisePriceConfig) {
		this.websiteId = websiteId;
		webAPI.defaults.headers['X-Api-Key'] = apiKey;
		webAPI.defaults.headers['X-Requested-With'] = 'XMLHttpRequest';
		if (ppSSOKey !== undefined) {
			webAPI.defaults.headers['X-Pp-Key'] = ppSSOKey;
		}
	}

	public warmupRequest(vin: string): Promise<WarmupResponse> {
		return webAPI.post(`/${this.websiteId}/vehicle/${vin}/warmup`)
			.then(res => res.data);
	}

	public getVin(params: PlateToVinServiceRequest): Promise<PlateToVinServiceResponseData> {
		return webAPI.get(`${this.websiteId}/plateToVin`, {
			params,
		})
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				if (error?.response?.status === 400) {
					return Promise.resolve(error?.response?.data);
				}
				throw error;
			});
	}

	public getTradeInValuation(params: TradeInValueRequest): Promise<TradeInAPIResponse> {
		const request = params;

		request.websiteId = Number(this.websiteId);
		return webAPI.get(`${this.websiteId}/tradePending`, {
			params,
		})
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public getTranslations(locale = Locales.EN): Promise<typeof Translations> {
		return webAPI.get(`${this.websiteId}/translations/${locale.split('-')[0]}`)
			.then(res => res.data);
	}

	public fetchVerificationCode(): Promise<VerificationCodeResponse> {
		return webAPI.get('/verificationCode')
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}

				throw error;
			});
	}

	public fetchCbcCreditScore(data: CreditRequest): Promise<CbcResponse> {
		return webAPI.post(`${this.websiteId}/customerCreditScore`, data)
			.then(res => res.data)
			.catch((error: AxiosError) => {
				if (error?.response?.status === 404) {
					return Promise.resolve(null);
				}
				throw error;
			});
	}

	public getSettings(): Promise<object> {
		return webAPI.get(`${this.websiteId}/configuration`)
			.then(res => res.data);
	}

	public getWebsiteInfo(): Promise<WebsiteResponse> {
		return webAPI.get(`/${this.websiteId}`)
			.then(res => res.data);
	}

	public getDealerInfo(dealerId: number): Promise<DealerResponse> {
		return webAPI.get(`${APP_CONFIG.API_URL}dealer/${dealerId}`)
			.then(res => res.data);
	}

	public checkButtonVisibility(vin: string): Promise<boolean> {
		return webAPI.get(`/${this.websiteId}/vehicle/${vin}/hasPrice`)
			.then(res => res.data);
	}
}
